import React, { useState } from 'react';
import { CheckCircle, AlertCircle, Package, DollarSign, X } from 'lucide-react';

/**
 * RequestProduct Component
 * FIXED: Sends JSON data (not FormData) to match API expectations
 * Removed image upload since API doesn't handle it in JSON mode
 */
const RequestProduct = () => {
  const [formData, setFormData] = useState({
    product_name: '',
    category: '',
    unit: '',
    description: '',
    estimated_price: '',
    supplier_suggestion: '',
    reason_for_request: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.product_name.trim()) {
      setMessage({ type: 'error', text: 'Product name is required' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      return;
    }

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const token = localStorage.getItem('ward_stock_token');
      
      // Send as JSON (not FormData) to match API expectations
      const response = await fetch('https://b1.envicblue.co.uk/api/product_requests.php', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setMessage({ 
          type: 'success', 
          text: 'Product request submitted successfully! Admin will review it soon.' 
        });
        
        // Reset form
        setFormData({
          product_name: '',
          category: '',
          unit: '',
          description: '',
          estimated_price: '',
          supplier_suggestion: '',
          reason_for_request: ''
        });
        
        window.scrollTo({ top: 0, behavior: 'smooth' });
        setTimeout(() => setMessage({ type: '', text: '' }), 5000);
      } else {
        setMessage({ 
          type: 'error', 
          text: data.message || 'Failed to submit request' 
        });
        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      }
    } catch (error) {
      console.error('Error submitting request:', error);
      setMessage({ 
        type: 'error', 
        text: 'Network error. Please try again.' 
      });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ 
      padding: '20px',
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
    }}>
      <div style={{ 
        maxWidth: '900px', 
        margin: '0 auto'
      }}>
        <div style={{ 
          textAlign: 'center', 
          marginBottom: '32px'
        }}>
          <div style={{ 
            fontSize: '56px', 
            marginBottom: '16px'
          }}>
            📦
          </div>
          <h2 style={{ 
            fontSize: '32px', 
            fontWeight: 'bold', 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            marginBottom: '8px'
          }}>
            Request a New Product
          </h2>
          <p style={{ color: '#6b7280', fontSize: '16px' }}>
            Can't find what you need? Let us know and we'll add it!
          </p>
        </div>

        {message.text && (
          <div style={{
            marginBottom: '24px',
            padding: '20px 24px',
            borderRadius: '16px',
            display: 'flex',
            alignItems: 'start',
            gap: '16px',
            background: message.type === 'success' 
              ? 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)' 
              : 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)',
            border: `2px solid ${message.type === 'success' ? '#10b981' : '#ef4444'}`,
            boxShadow: '0 8px 20px rgba(0,0,0,0.1)'
          }}>
            {message.type === 'success' ? (
              <CheckCircle style={{ width: '28px', height: '28px', color: '#065f46', flexShrink: 0 }} />
            ) : (
              <AlertCircle style={{ width: '28px', height: '28px', color: '#991b1b', flexShrink: 0 }} />
            )}
            <span style={{ 
              flex: 1, 
              color: message.type === 'success' ? '#065f46' : '#991b1b', 
              fontWeight: '500', 
              fontSize: '15px' 
            }}>
              {message.text}
            </span>
            <button 
              onClick={() => setMessage({ type: '', text: '' })} 
              style={{ 
                background: 'none', 
                border: 'none', 
                cursor: 'pointer', 
                padding: '4px', 
                color: message.type === 'success' ? '#065f46' : '#991b1b' 
              }}
            >
              <X style={{ width: '20px', height: '20px' }} />
            </button>
          </div>
        )}

        <div style={{ 
          background: 'white', 
          borderRadius: '24px', 
          padding: '40px', 
          boxShadow: '0 20px 60px rgba(0,0,0,0.15)' 
        }}>
          <form onSubmit={handleSubmit}>
            {/* Product Name */}
            <div style={{ marginBottom: '24px' }}>
              <label style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '8px', 
                fontSize: '14px', 
                fontWeight: '600', 
                color: '#374151', 
                marginBottom: '10px' 
              }}>
                <Package style={{ width: '18px', height: '18px' }} />
                Product Name <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <input 
                type="text" 
                name="product_name" 
                value={formData.product_name} 
                onChange={handleInputChange} 
                style={{ 
                  width: '100%', 
                  padding: '14px 16px', 
                  border: '2px solid #e5e7eb', 
                  borderRadius: '10px', 
                  fontSize: '15px', 
                  outline: 'none' 
                }} 
                placeholder="e.g., Digital Thermometer" 
                required 
              />
            </div>

            {/* Category and Unit */}
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: window.innerWidth < 768 ? '1fr' : '1fr 1fr', 
              gap: '20px', 
              marginBottom: '24px' 
            }}>
              <div>
                <label style={{ 
                  fontSize: '14px', 
                  fontWeight: '600', 
                  color: '#374151', 
                  marginBottom: '10px', 
                  display: 'block' 
                }}>
                  Category
                </label>
                <select 
                  name="category" 
                  value={formData.category} 
                  onChange={handleInputChange} 
                  style={{ 
                    width: '100%', 
                    padding: '14px 16px', 
                    border: '2px solid #e5e7eb', 
                    borderRadius: '10px', 
                    fontSize: '15px' 
                  }}
                >
                  <option value="">Select category</option>
                  <option value="Medical Supplies">Medical Supplies</option>
                  <option value="Medications">Medications</option>
                  <option value="Equipment">Equipment</option>
                  <option value="Personal Protective Equipment">PPE</option>
                  <option value="Cleaning Supplies">Cleaning Supplies</option>
                  <option value="Office Supplies">Office Supplies</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div>
                <label style={{ 
                  fontSize: '14px', 
                  fontWeight: '600', 
                  color: '#374151', 
                  marginBottom: '10px', 
                  display: 'block' 
                }}>
                  Unit
                </label>
                <input 
                  type="text" 
                  name="unit" 
                  value={formData.unit} 
                  onChange={handleInputChange} 
                  style={{ 
                    width: '100%', 
                    padding: '14px 16px', 
                    border: '2px solid #e5e7eb', 
                    borderRadius: '10px', 
                    fontSize: '15px' 
                  }} 
                  placeholder="Box, Bottle, Pack" 
                />
              </div>
            </div>

            {/* Description */}
            <div style={{ marginBottom: '24px' }}>
              <label style={{ 
                fontSize: '14px', 
                fontWeight: '600', 
                color: '#374151', 
                marginBottom: '10px', 
                display: 'block' 
              }}>
                Description
              </label>
              <textarea 
                name="description" 
                value={formData.description} 
                onChange={handleInputChange} 
                rows="4" 
                style={{ 
                  width: '100%', 
                  padding: '14px 16px', 
                  border: '2px solid #e5e7eb', 
                  borderRadius: '10px', 
                  fontSize: '15px', 
                  resize: 'vertical', 
                  fontFamily: 'inherit' 
                }} 
                placeholder="Detailed description..." 
              />
            </div>

            {/* Estimated Price and Supplier */}
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: window.innerWidth < 768 ? '1fr' : '1fr 1fr', 
              gap: '20px', 
              marginBottom: '24px' 
            }}>
              <div>
                <label style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px', 
                  fontSize: '14px', 
                  fontWeight: '600', 
                  color: '#374151', 
                  marginBottom: '10px' 
                }}>
                  <DollarSign style={{ width: '18px', height: '18px' }} />
                  Estimated Price
                </label>
                <input 
                  type="number" 
                  name="estimated_price" 
                  value={formData.estimated_price} 
                  onChange={handleInputChange} 
                  step="0.01" 
                  min="0" 
                  style={{ 
                    width: '100%', 
                    padding: '14px 16px', 
                    border: '2px solid #e5e7eb', 
                    borderRadius: '10px', 
                    fontSize: '15px' 
                  }} 
                  placeholder="0.00" 
                />
              </div>

              <div>
                <label style={{ 
                  fontSize: '14px', 
                  fontWeight: '600', 
                  color: '#374151', 
                  marginBottom: '10px', 
                  display: 'block' 
                }}>
                  Suggested Supplier
                </label>
                <input 
                  type="text" 
                  name="supplier_suggestion" 
                  value={formData.supplier_suggestion} 
                  onChange={handleInputChange} 
                  style={{ 
                    width: '100%', 
                    padding: '14px 16px', 
                    border: '2px solid #e5e7eb', 
                    borderRadius: '10px', 
                    fontSize: '15px' 
                  }} 
                  placeholder="Supplier name" 
                />
              </div>
            </div>

            {/* Reason for Request */}
            <div style={{ marginBottom: '28px' }}>
              <label style={{ 
                fontSize: '14px', 
                fontWeight: '600', 
                color: '#374151', 
                marginBottom: '10px', 
                display: 'block' 
              }}>
                Reason for Request
              </label>
              <textarea 
                name="reason_for_request" 
                value={formData.reason_for_request} 
                onChange={handleInputChange} 
                rows="3" 
                style={{ 
                  width: '100%', 
                  padding: '14px 16px', 
                  border: '2px solid #e5e7eb', 
                  borderRadius: '10px', 
                  fontSize: '15px', 
                  resize: 'vertical', 
                  fontFamily: 'inherit' 
                }} 
                placeholder="Why is this product needed?" 
              />
            </div>

            {/* Submit and Clear Buttons */}
            <div style={{ 
              display: 'flex', 
              gap: '16px', 
              flexDirection: window.innerWidth < 768 ? 'column' : 'row' 
            }}>
              <button 
                type="submit" 
                disabled={loading} 
                style={{ 
                  flex: 1, 
                  padding: '16px 32px', 
                  background: loading ? '#9ca3af' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
                  color: 'white', 
                  border: 'none', 
                  borderRadius: '12px', 
                  fontSize: '16px', 
                  fontWeight: '600', 
                  cursor: loading ? 'not-allowed' : 'pointer', 
                  boxShadow: loading ? 'none' : '0 8px 20px rgba(102, 126, 234, 0.4)', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  gap: '10px' 
                }}
              >
                {loading ? '⏳ Submitting...' : '✓ Submit Request'}
              </button>
              
              <button 
                type="button" 
                onClick={() => { 
                  setFormData({ 
                    product_name: '', 
                    category: '', 
                    unit: '', 
                    description: '', 
                    estimated_price: '', 
                    supplier_suggestion: '', 
                    reason_for_request: '' 
                  }); 
                  setMessage({ type: '', text: '' }); 
                }} 
                style={{ 
                  padding: '16px 32px', 
                  background: 'white', 
                  color: '#6b7280', 
                  border: '2px solid #e5e7eb', 
                  borderRadius: '12px', 
                  fontSize: '16px', 
                  fontWeight: '600', 
                  cursor: 'pointer' 
                }}
              >
                Clear Form
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default RequestProduct;
